import { useCallback } from 'react';

import { ensureDefined } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import { userFriendlyErrors } from '../../../../../utils/back-errors.util';
import type { BackError } from '../../../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { addressForm2ApiMapper } from '../../../../Address/mappers/address.form2api.mapper';
import type { AddressForm_Address } from '../../../../Address/fragments/address.fragment';
import type { AddressFormTab } from '../../../../Address/enums/tab.enum';

import { useUpdateThirdPartyAddressMutation } from './useUpdateThirdPartyAddressMutation.hook';
import { useFindAddressIndexById } from './useFindIndexById.hook';

interface Params {
  readonly addressId?: Id | null;
  readonly handleUpdateSuccess: () => void;
  readonly thirdPartyId: Id;
  readonly useDirectMutation: boolean;
}

interface FormikArrayActions {
  readonly updateInForm: (
    index: number,
    value: Readonly<AddressForm_Address>,
  ) => void;
}

export const useUpdateThirdPartyAddressSubmit = ({
  addressId,
  handleUpdateSuccess,
  thirdPartyId,
  useDirectMutation,
}: Readonly<Params>) => {
  const { mutate: updateMutation } = useUpdateThirdPartyAddressMutation();

  const findIndexById = useFindAddressIndexById();

  const handleSubmitFactory = useCallback(
    ({ updateInForm }: Readonly<FormikArrayActions>) =>
      ({
        values,
        memos,
        formikHelpers: { setSubmitting },
        setWizardError,
      }: Readonly<
        WizardSubmitHandlerParams<AddressForm_Address, AddressFormTab>
      >) => {
        const { addressId: definedAddressId } = ensureDefined({
          addressId,
        });

        setWizardError(undefined);

        const onError = (error: unknown) => {
          setWizardError({ messages: userFriendlyErrors(error as BackError) });
          setSubmitting(false);
        };

        const onSuccess = () => {
          const index = findIndexById(definedAddressId);

          if (index === -1) {
            setWizardError({ messages: ["L'adresse n'existe pas"] });
            setSubmitting(false);
            return;
          }

          updateInForm(index, values);

          handleUpdateSuccess();
          setSubmitting(false);
        };

        if (useDirectMutation) {
          updateMutation(
            {
              thirdPartyId,
              addressId: definedAddressId,
              update: addressForm2ApiMapper(values),
              versionData: memoForm2ApiMapper(memos),
            },
            {
              onError,
              onSuccess,
            },
          );

          return undefined;
        }

        onSuccess();

        return undefined;
      },
    [
      addressId,
      findIndexById,
      handleUpdateSuccess,
      thirdPartyId,
      updateMutation,
      useDirectMutation,
    ],
  );

  return { handleSubmitFactory };
};
